import * as d3 from "d3";

// custom barchart
export function CustomBarchart(zValues, labels, {
    width = 600,
    height = 320,
    margin = { top: 80, bottom: 60, left: 70, right: 40 },
    labelTextSize = "13px",
    positiveColor = "#2b8cff",
    negativeColor = "#ff6b6b",
    threshold = 1.96,
    showThresholdLines = true,
    showValueLabels = true,
    showCI = false,
    se = null,
    ciMultiplier = 1.96,
    barOpacity = 0.9,
    barStroke = "#333",
    barStrokeWidth = 1,
    nonSigPairs = [],
    pairBrackets = [],
    betas = null,
    covFull = null,
    yAxisLabel = "z value",
    legendText = null
} = {}) {
    function normalCdf(x) {
        const t = 1 / (1 + 0.2316419 * x);
        const a1 = 0.319381530, a2 = -0.356563782, a3 = 1.781477937, a4 = -1.821255978, a5 = 1.330274429;
        const pdf = Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
        const poly = ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t;
        return 1 - pdf * poly;
    }

    const allBrackets = [
        ...pairBrackets,
        ...nonSigPairs.map(([i, j]) => {
            if (!betas || !covFull || !se) return { i, j, p: NaN };
            const diff = betas[i] - betas[j];
            const seDiff = Math.sqrt(covFull[i][i] + covFull[j][j] - 2 * covFull[i][j]);
            if (seDiff === 0) return { i, j, p: NaN };
            const z = diff / seDiff;
            return { i, j, p: 2 * (1 - normalCdf(Math.abs(z))) };
        })
    ];

    const TICK_H      = 8;
    const LEVEL_H     = 26;
    const BASE_MARGIN = 10;
    const TEXT_H      = 14;

    const sortedBrackets = [...allBrackets].sort((a, b) => {
        const aSig = !isNaN(a.p) && a.p < 0.05 ? 1 : 0;
        const bSig = !isNaN(b.p) && b.p < 0.05 ? 1 : 0;
        if (aSig !== bSig) return aSig - bSig;
        return (a.j - a.i) - (b.j - b.i);
    });
    const placed = [];
    let sigBaseLevel = 0;
    sortedBrackets.forEach(br => {
        const brIsSig = !isNaN(br.p) && br.p < 0.05;
        let level = brIsSig ? sigBaseLevel : 0;
        while (placed.some(q => q.level === level && q.i <= br.j && q.j >= br.i)) level++;
        placed.push({ ...br, level });
        if (!brIsSig) sigBaseLevel = Math.max(sigBaseLevel, level + 1);
    });
    const maxLevel = placed.length > 0 ? Math.max(...placed.map(b => b.level)) : -1;

    const bracketAreaH = maxLevel >= 0 ? (maxLevel + 1) * LEVEL_H + TICK_H + TEXT_H + BASE_MARGIN : 0;

    const N = zValues.length;
    if (!labels || labels.length !== N) {
        labels = zValues.map((_, i) => `M${i+1}`);
    }

    // compute domain for y
    const zMin = Math.min(...zValues.filter(v => !isNaN(v)), -threshold * 1.5);
    const zMax = Math.max(...zValues.filter(v => !isNaN(v)), threshold * 1.5);
    const pad = Math.max(0.5, (zMax - zMin) * 0.1);
    const yDomainMin    = Math.min(zMin - pad, -threshold * 1.2);
    const yDomainMaxRaw = Math.max(zMax + pad, threshold * 1.2);

    const highestBarTop = zValues.reduce((mx, v, i) => {
        if (isNaN(v)) return mx;
        const top = (showCI && Array.isArray(se) && se.length > i && !isNaN(se[i]))
            ? v + ciMultiplier * se[i] : v;
        return Math.max(mx, top);
    }, -Infinity);
    const targetPx  = bracketAreaH + 8;
    const yDomainMax = (bracketAreaH > 0 && height > targetPx && isFinite(highestBarTop))
        ? Math.max(
            (height * highestBarTop - targetPx * yDomainMin) / (height - targetPx),
            yDomainMaxRaw
          )
        : yDomainMaxRaw;

    let maxLabelWidth = 0;
    for (let i = 0; i < labels.length; i++) {
        maxLabelWidth = Math.max(maxLabelWidth, getLabelWidth(labels[i], labelTextSize));
    }

    const approxBand = width / Math.max(1, N);
    if (maxLabelWidth > approxBand) {
        margin.bottom += Math.sqrt((maxLabelWidth * maxLabelWidth) / 2);
    }

    // svg
    const svg = d3.create("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom);

    const main = svg.append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

    // scales
    const xScale = d3.scaleBand()
        .domain(labels)
        .range([0, width])
        .padding(0.25);

    const yScale = d3.scaleLinear()
        .domain([yDomainMin, yDomainMax])
        .nice()
        .range([height, 0]);

    // grid lines
    main.selectAll("line.hgrid")
        .data(yScale.ticks())
        .enter()
        .append("line")
        .attr("class", "hgrid")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", d => yScale(d))
        .attr("y2", d => yScale(d))
        .attr("stroke", "#ddd");

    // axes
    const xAxisG = main.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(xScale));

    xAxisG.selectAll("text").attr("font-size", labelTextSize);
    if (maxLabelWidth > xScale.bandwidth()) {
        xAxisG.selectAll("text")
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-45)");
    }

    main.append("g")
        .call(d3.axisLeft(yScale))
        .selectAll("text")
        .attr("font-size", labelTextSize);

    svg.append("text")
        .attr("transform", `rotate(-90 ${margin.left/2} ${height/2 + margin.top}) translate(-20,${height/2 + margin.top})`)
        .attr("font-size", labelTextSize)
        .text(yAxisLabel);

    main.append("line")
        .attr("x1", 0).attr("x2", width)
        .attr("y1", yScale(0)).attr("y2", yScale(0))
        .attr("stroke", "#333")
        .attr("stroke-width", 1.5);

    // threshold lines
    if (showThresholdLines) {
        const yPlus = yScale(threshold);
        const yMinus = yScale(-threshold);
        main.append("line")
            .attr("x1", 0).attr("x2", width)
            .attr("y1", yPlus).attr("y2", yPlus)
            .attr("stroke", "#aaa").attr("stroke-dasharray", "6,4").attr("stroke-width", 1);
        main.append("line")
            .attr("x1", 0).attr("x2", width)
            .attr("y1", yMinus).attr("y2", yMinus)
            .attr("stroke", "#aaa").attr("stroke-dasharray", "6,4").attr("stroke-width", 1);

        main.append("text")
            .attr("x", width - 60).attr("y", yPlus - 6)
            .attr("fill", "#aaa").attr("font-size", "12px").text(`+${threshold}`);
        main.append("text")
            .attr("x", width - 60).attr("y", yMinus + 14)
            .attr("fill", "#aaa").attr("font-size", "12px").text(`-${threshold}`);
    }

    const bars = main.selectAll("g.bar")
        .data(labels)
        .enter()
        .append("g")
        .attr("class", "bar")
        .attr("transform", d => `translate(${xScale(d)},0)`);

    // draw CI lines
    if (showCI && Array.isArray(se) && se.length === N) {
        const halfW = xScale.bandwidth() / 2;
        bars.append("line")
            .attr("class", "ci-line")
            .attr("x1", halfW).attr("x2", halfW)
            .attr("y1", (d, i) => yScale(zValues[i] + ciMultiplier * se[i]))
            .attr("y2", (d, i) => yScale(zValues[i] - ciMultiplier * se[i]))
            .attr("stroke", "#444")
            .attr("stroke-width", 1.2)
            .attr("stroke-opacity", 0.9);

        bars.append("line")
            .attr("x1", halfW - (xScale.bandwidth() * 0.15)).attr("x2", halfW + (xScale.bandwidth() * 0.15))
            .attr("y1", (d, i) => yScale(zValues[i] + ciMultiplier * se[i]))
            .attr("y2", (d, i) => yScale(zValues[i] + ciMultiplier * se[i]))
            .attr("stroke", "#444").attr("stroke-width", 1.2);

        bars.append("line")
            .attr("x1", halfW - (xScale.bandwidth() * 0.15)).attr("x2", halfW + (xScale.bandwidth() * 0.15))
            .attr("y1", (d, i) => yScale(zValues[i] - ciMultiplier * se[i]))
            .attr("y2", (d, i) => yScale(zValues[i] - ciMultiplier * se[i]))
            .attr("stroke", "#444").attr("stroke-width", 1.2);

        const ciLabelStyle = sel => sel
            .attr("text-anchor", "middle")
            .attr("font-size", "12px")
            .attr("font-weight", "700")
            .attr("fill", "#111")
            .style("stroke", "white")
            .style("stroke-width", "3px")
            .style("stroke-linejoin", "round")
            .style("paint-order", "stroke");

        ciLabelStyle(bars.append("text"))
            .attr("x", halfW)
            .attr("y", (d, i) => yScale(zValues[i] + ciMultiplier * se[i]) - 5)
            .text((d, i) => isNaN(se[i]) ? "" : (Math.round((zValues[i] + ciMultiplier * se[i]) * 100) / 100).toFixed(2));

        ciLabelStyle(bars.append("text"))
            .attr("x", halfW)
            .attr("y", (d, i) => yScale(zValues[i] - ciMultiplier * se[i]) + 14)
            .text((d, i) => isNaN(se[i]) ? "" : (Math.round((zValues[i] - ciMultiplier * se[i]) * 100) / 100).toFixed(2));
    }

    // draw bars
    const zeroY = yScale(0);
    bars.append("rect")
        .attr("x", 0)
        .attr("y", (d, i) => {
            const v = zValues[i];
            return v >= 0 ? yScale(v) : zeroY;
        })
        .attr("width", xScale.bandwidth())
        .attr("height", (d, i) => {
            const v = zValues[i];
            return isNaN(v) ? 0 : Math.abs(zScaleHeight(yScale, zeroY, v));
        })
        .attr("fill", (d, i) => (zValues[i] >= 0 ? positiveColor : negativeColor))
        .attr("opacity", barOpacity)
        .style("stroke", barStroke)
        .attr("stroke-width", barStrokeWidth);

    function zScaleHeight(yScale, zeroY, v) {
        if (isNaN(v)) return 0;
        return zeroY - yScale(v);
    }

    if (showValueLabels) {
        const hasCI = showCI && Array.isArray(se) && se.length === N;
        const valLabelX  = hasCI ? xScale.bandwidth() - 6 : xScale.bandwidth() / 2;
        const valAnchor  = hasCI ? "end" : "middle";

        bars.append("text")
            .attr("x", valLabelX)
            .attr("y", (d, i) => {
                const v = zValues[i];
                if (isNaN(v)) return zeroY - 6;
                return v >= 0 ? yScale(v) - 6 : yScale(v) + 14;
            })
            .attr("text-anchor", valAnchor)
            .attr("font-size", "12px")
            .attr("font-weight", "700")
            .attr("fill", "#111")
            .style("stroke", "white")
            .style("stroke-width", "3px")
            .style("stroke-linejoin", "round")
            .style("paint-order", "stroke")
            .text((d, i) => isNaN(zValues[i]) ? "NaN" : (Math.round(zValues[i] * 1000) / 1000).toString());
    }

    const titleText = legendText !== null ? legendText : `z‑scores (threshold ±${threshold})`;
    const titleWidth = getLabelWidth(titleText, labelTextSize);
    svg.append("text")
        .attr("x", (margin.left + width + margin.right) / 2 - titleWidth / 2)
        .attr("y", margin.top / 2)
        .attr("font-size", labelTextSize)
        .attr("text-anchor", "start")
        .text(titleText);

    if (placed.length > 0) {
        const barTopPx = (idx) => {
            const z   = zValues[idx];
            const top = showCI && se && se[idx] ? z + ciMultiplier * se[idx] : z;
            return yScale(Math.max(top, 0));
        };

        const globalHighestPx = Math.min(...Array.from({ length: N }, (_, k) => barTopPx(k)));

        placed.forEach(({ i, j, p, level }) => {
            const x1 = xScale(labels[i]) + xScale.bandwidth() / 2;
            const x2 = xScale(labels[j]) + xScale.bandwidth() / 2;

            const bracketY = globalHighestPx - BASE_MARGIN - level * LEVEL_H;

            const isSig = !isNaN(p) && p < 0.05;
            const color = isSig ? "#2563eb" : "#dc2626";
            const dash  = isSig ? "none" : "4,3";

            const bPath = d3.path();
            bPath.moveTo(x1, bracketY);
            bPath.lineTo(x1, bracketY - TICK_H);
            bPath.lineTo(x2, bracketY - TICK_H);
            bPath.lineTo(x2, bracketY);

            main.append("path")
                .attr("d", bPath.toString())
                .attr("stroke", color)
                .attr("stroke-width", 1.5)
                .attr("stroke-dasharray", dash)
                .attr("fill", "none");

            const stars  = !isNaN(p) ? (p < 0.001 ? " ***" : p < 0.01 ? " **" : p < 0.05 ? " *" : "") : "";
            const pLabel = isNaN(p) ? "n/a" : (p < 0.001 ? "p < 0.001" : `p = ${p.toFixed(3)}`);
            main.append("text")
                .attr("x", (x1 + x2) / 2)
                .attr("y", bracketY - TICK_H - 3)
                .attr("text-anchor", "middle")
                .attr("font-size", "10px")
                .attr("font-weight", "600")
                .attr("fill", color)
                .text(pLabel + stars);
        });
    }

    return svg.node();
}

// measure label width
function getLabelWidth(text, fontSize) {
    const dummySvg = d3.select("body").append("svg");
    dummySvg.append("text")
        .attr("x", -100000)
        .attr("y", -100000)
        .attr("font-size", fontSize)
        .text(text);
    const size = dummySvg.node().getBBox();
    dummySvg.remove();
    return size.width;
}
